import { toast } from 'react-toastify'

/**
 * Get cookie value by name
 * @param {string} name - Cookie name
 * @returns {string|null} - Cookie value or null if not found
 */
const getCookie = (name) => {
  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)
  if (parts.length === 2) return parts.pop().split(';').shift()
  return null
}

/**
 * Clear cookie by setting it to expire in the past
 * @param {string} name - Cookie name
 */
const clearCookie = (name) => {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; domain=.payhelm.com; path=/`
}

/**
 * Handle ChannelAdvisor OAuth error parameters and cookies
 *
 * @param {URLSearchParams} params - URL search parameters
 * @returns {boolean} - True if an error was handled, false otherwise
 */
export const handleChannelAdvisorErrors = (params) => {
  // Check URL parameters first (for legacy support)
  const urlError = params.get('channeladvisor_error')

  // Check error cookie (primary method used by backend)
  const cookieError = getCookie('channeladvisor_error')

  const hasError = urlError || (cookieError === 'true')

  if (!hasError) {
    return false
  }

  // Clear the error cookie if it exists
  if (cookieError === 'true') {
    clearCookie('channeladvisor_error')
  }

  // Handle ChannelAdvisor OAuth errors
  let errorMessage = 'ChannelAdvisor connection failed. Please try again or check your credentials.'

  // Handle specific URL parameter errors (legacy)
  if (urlError) {
    switch (urlError) {
      case 'no_fragment':
        errorMessage = 'ChannelAdvisor connection failed: No authorization data received.'
        break
      case 'no_access_token':
        errorMessage = 'ChannelAdvisor connection failed: No access token received.'
        break
      case 'invalid_state':
        errorMessage = 'ChannelAdvisor connection failed: Invalid security token.'
        break
      case 'access_denied':
        errorMessage = 'ChannelAdvisor connection was denied. Please try again and grant the required permissions.'
        break
      default:
        errorMessage = `ChannelAdvisor connection failed: ${urlError}`
    }
  }

  toast.error(errorMessage, {
    position: 'top-right',
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
  })

  return true
}

/**
 * Handle ChannelAdvisor OAuth success parameters and cookies
 *
 * @param {URLSearchParams} params - URL search parameters
 * @returns {boolean} - True if success was handled, false otherwise
 */
export const handleChannelAdvisorSuccess = (params) => {
  // Check URL parameters first (for legacy support)
  const urlSuccess = params.get('channeladvisor_connected')

  // Check success cookie (primary method used by backend)
  const cookieSuccess = getCookie('channeladvisor_connected')

  const hasSuccess = (urlSuccess === 'true') || (cookieSuccess === 'true')

  if (!hasSuccess) {
    return false
  }

  // Clear the success cookie if it exists
  if (cookieSuccess === 'true') {
    clearCookie('channeladvisor_connected')
  }

  toast.success('ChannelAdvisor (Rithum) connected successfully!', {
    position: 'top-right',
    autoClose: 4000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
  })

  return true
}

/**
 * Initialize ChannelAdvisor status checking on page load
 * This should be called when the integrations page loads
 */
export const initializeChannelAdvisorStatusCheck = () => {
  // Create URLSearchParams from current page URL for legacy support
  const params = new URLSearchParams(window.location.search)

  // Check for both errors and success
  const errorHandled = handleChannelAdvisorErrors(params)
  const successHandled = handleChannelAdvisorSuccess(params)

  // Clean up URL parameters if they were handled
  if (errorHandled || successHandled) {
    // Remove ChannelAdvisor parameters from URL without page reload
    const url = new URL(window.location)
    url.searchParams.delete('channeladvisor_error')
    url.searchParams.delete('channeladvisor_connected')

    // Update URL without reloading the page
    window.history.replaceState({}, '', url.toString())
  }

  return { errorHandled, successHandled }
}
